/* Orya — language-aware product media carousel.
 * Tags each product media slide as EN / PT / both from the media alt text
 * (alt containing "#en" or "#pt") and shows only the slides that match the
 * language selected in the header (EN/PT switch). Falls back to showing
 * everything when a product has no tagged images for the active language.
 */
(() => {
  const SUPPORTED = ['en', 'pt'];

  const urlLang = () => {
    try {
      const value = new URL(window.location.href).searchParams.get('lang');
      return SUPPORTED.includes(value) ? value : null;
    } catch (_) { return null; }
  };

  const currentLang = () => {
    try {
      const saved = window.localStorage.getItem('orya-language');
      return urlLang() || (SUPPORTED.includes(saved) ? saved : 'en');
    } catch (_) { return urlLang() || 'en'; }
  };

  const langOf = (alt) => {
    const a = (alt || '').toLowerCase();
    if (a.indexOf('#pt') !== -1) return 'pt';
    if (a.indexOf('#en') !== -1) return 'en';
    return 'both';
  };

  const init = async () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const oldCarousel = document.querySelector('.orya-product-media [data-orya-carousel]');
    if (!oldCarousel) return;

    let media;
    try {
      const res = await fetch('/products/' + match[1] + '.js');
      if (!res.ok) return;
      media = (await res.json()).media || [];
    } catch (_) { return; }
    if (!media.length) return;

    /* Take over: clone strips the old script's listeners; its timer keeps
     * running on detached nodes, which has no visible effect. */
    const carousel = oldCarousel.cloneNode(true);
    oldCarousel.replaceWith(carousel);

    const track = carousel.querySelector('[data-orya-carousel-track]');
    if (!track) return;
    let slides = Array.from(track.querySelectorAll('[data-orya-carousel-slide]'));

    /* The section renders at most 8 media; append the rest as image slides. */
    for (let i = slides.length; i < media.length; i++) {
      const m = media[i];
      const src = m.src || (m.preview_image && m.preview_image.src);
      if (!src || (m.media_type && m.media_type !== 'image')) continue;
      const sep = src.indexOf('?') === -1 ? '?' : '&';
      const fig = document.createElement('figure');
      fig.className = 'orya-product-media-card';
      fig.setAttribute('data-orya-carousel-slide', '');
      const a = document.createElement('a');
      a.className = 'orya-product-media-link';
      a.href = src + sep + 'width=2400';
      a.target = '_blank';
      a.rel = 'noopener';
      a.setAttribute('aria-label', 'Open larger preview / Abrir pré-visualização ' + (i + 1));
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = src + sep + 'width=1600';
      img.alt = '';
      a.appendChild(img);
      fig.appendChild(a);
      track.appendChild(fig);
    }
    slides = Array.from(track.querySelectorAll('[data-orya-carousel-slide]'));

    /* Tag every slide with its language, taken from the media alt text. */
    slides.forEach((slide, i) => {
      slide.dataset.oryaMediaLang = media[i] ? langOf(media[i].alt) : 'both';
    });

    const prev = carousel.querySelector('[data-orya-carousel-prev]');
    const next = carousel.querySelector('[data-orya-carousel-next]');
    const dotsWrap = carousel.querySelector('[data-orya-carousel-dots]');
    const count = carousel.querySelector('[data-orya-carousel-count]');
    if (count) count.setAttribute('data-no-localize', '');

    let visible = slides;
    let index = 0;
    let paused = false;

    const render = () => {
      track.style.transform = 'translateX(' + (-index * 100) + '%)';
      if (dotsWrap) {
        Array.from(dotsWrap.querySelectorAll('button')).forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
          dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
        });
      }
      if (count) count.textContent = (index + 1) + ' / ' + visible.length;
    };

    const go = (nextIndex, userAction) => {
      if (!visible.length) return;
      index = (nextIndex + visible.length) % visible.length;
      if (userAction) paused = true;
      render();
    };

    const buildDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      if (visible.length < 2) return;
      visible.forEach((_, dotIndex) => {
        const dot = document.createElement('button');
        dot.className = 'orya-carousel-dot';
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Show preview / Mostrar pré-visualização ' + (dotIndex + 1));
        dot.addEventListener('click', () => go(dotIndex, true));
        dotsWrap.appendChild(dot);
      });
    };

    const applyLanguage = (langOverride) => {
      const lang = SUPPORTED.includes(langOverride) ? langOverride : currentLang();
      let matching = slides.filter((slide) => {
        const slideLang = slide.dataset.oryaMediaLang || 'both';
        return slideLang === 'both' || slideLang === lang;
      });
      if (!matching.length) matching = slides;
      slides.forEach((slide) => { slide.style.display = matching.indexOf(slide) !== -1 ? '' : 'none'; });
      visible = matching;
      index = 0;
      buildDots();
      render();
      const many = visible.length > 1;
      if (prev) prev.style.display = many ? '' : 'none';
      if (next) next.style.display = many ? '' : 'none';
      if (count) count.style.display = many ? '' : 'none';
      if (dotsWrap) dotsWrap.style.display = many ? '' : 'none';
    };

    if (prev) prev.addEventListener('click', () => go(index - 1, true));
    if (next) next.addEventListener('click', () => go(index + 1, true));
    carousel.addEventListener('mouseenter', () => { paused = true; });
    carousel.addEventListener('focusin', () => { paused = true; });

    applyLanguage();

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-orya-lang-button]');
      if (button) window.setTimeout(() => applyLanguage(button.dataset.oryaLangButton), 0);
    });

    if (carousel.dataset.autoplay === 'true') {
      window.setInterval(() => {
        if (!paused && visible.length > 1) go(index + 1, false);
      }, 4500);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
